"use client";

import { useTranslations } from "next-intl";
import { ChevronRight } from "lucide-react";
import { getLocaleConfig, type Locale } from "@/config/locale";
import { getCurrencyNarrowSymbol } from "@/lib/currency-display";
import { FooterLocaleFlag } from "@/ui/components/footer-region-flags";

function getCurrencyName(code: string, locale: string): string {
	try {
		return new Intl.DisplayNames(locale, { type: "currency" }).of(code) ?? code;
	} catch {
		return code;
	}
}

export interface AccordionPanelProps {
	currentLocale: Locale;
	currentChannel: string;
	currentCurrency: string;
	onOpenLanguage: () => void;
	onOpenRegion: () => void;
	onOpenCurrency: () => void;
}

export function AccordionPanel({
	currentLocale,
	currentChannel,
	currentCurrency,
	onOpenLanguage,
	onOpenRegion,
	onOpenCurrency,
}: AccordionPanelProps) {
	const t = useTranslations("localeChannel");
	const localeConfig = getLocaleConfig(currentLocale);
	const isUs = currentChannel.toLowerCase() === "us";
	const regionKey = isUs ? "regionAmericas" : "regionWorldwide";
	const regionSubKey = isUs ? "regionAmericasSub" : "regionWorldwideSub";
	const currencySymbol = getCurrencyNarrowSymbol(currentCurrency, currentLocale);
	const currencyName = getCurrencyName(currentCurrency, currentLocale);

	return (
		<div className="flex h-full flex-col">
			<div className="from-muted/30 border-b border-border bg-gradient-to-b to-transparent pb-4 pl-5 pr-16 pt-5">
				<p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
					{t("title")}
				</p>
				<p className="mt-1 text-sm font-semibold text-foreground">
					{t(regionKey)} · {currentCurrency}
				</p>
			</div>

			<div className="flex flex-col p-3">
				<AccordionRow
					icon={<FooterLocaleFlag locale={currentLocale} className="h-4 w-6 rounded-[2px]" />}
					label={t("languageLabel")}
					value={localeConfig.nativeName}
					onClick={onOpenLanguage}
				/>
				<div className="bg-border/60 mx-3 h-px" />
				<AccordionRow
					icon={<span className="text-lg leading-none">{isUs ? "🌎" : "🌍"}</span>}
					label={t("regionLabel")}
					value={t(regionKey)}
					subValue={t(regionSubKey)}
					onClick={onOpenRegion}
				/>
				<div className="bg-border/60 mx-3 h-px" />
				<AccordionRow
					icon={<span className="text-sm font-bold tabular-nums">{currencySymbol}</span>}
					label={t("currencyLabel")}
					value={`${currentCurrency} · ${currencyName}`}
					onClick={onOpenCurrency}
				/>
			</div>

			<div className="bg-muted/30 mt-auto border-t border-border px-5 py-3">
				<p className="text-[11px] leading-relaxed text-muted-foreground">
					💡 {t("footerHintRegion")} {t("footerHintCurrency")}
				</p>
			</div>
		</div>
	);
}

function AccordionRow({
	icon,
	label,
	value,
	subValue,
	onClick,
}: {
	icon: React.ReactNode;
	label: string;
	value: string;
	subValue?: string;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={label}
			className="hover:bg-muted/50 flex min-h-[64px] items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
		>
			<span className="bg-muted/40 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">{icon}</span>
			<span className="min-w-0 flex-1">
				<span className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
					{label}
				</span>
				<span className="block truncate text-sm font-semibold text-foreground">{value}</span>
				{subValue ? (
					<span className="block truncate text-[11px] text-muted-foreground">{subValue}</span>
				) : null}
			</span>
			<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
		</button>
	);
}
